import React, { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Camera, X, ChevronRight, ChevronLeft, Copy } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import useSEO from "@/hooks/useSEO";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPORTS = [
  { id: "football", label: "Football", icon: "\uD83C\uDFC8" },
  { id: "basketball", label: "Basketball", icon: "\uD83C\uDFC0" },
  { id: "baseball", label: "Baseball", icon: "\u26BE" },
  { id: "hockey", label: "Hockey", icon: "\uD83C\uDFD2" },
  { id: "soccer", label: "Soccer", icon: "\u26BD" },
];

const POSITIONS = {
  football: ["QB", "Running Back", "Wide Receiver", "Linebacker", "Kicker"],
  basketball: ["Point Guard", "Shooting Guard", "Small Forward", "Power Forward", "Center"],
  baseball: ["Pitcher", "Catcher", "Shortstop", "Outfielder", "First Base"],
  hockey: ["Center", "Left Wing", "Defenseman", "Goalie"],
  soccer: ["Striker", "Midfielder", "Defender", "Goalkeeper"],
};

const STYLES = [
  { id: "action", label: "Action Shot", desc: "Dynamic mid-motion pose with energy" },
  { id: "portrait", label: "Portrait", desc: "Classic card style, confident pose" },
  { id: "retro", label: "Retro", desc: "1980s/90s vintage card aesthetic" },
];

export default function CreatePage() {
  useSEO({ title: "Create Your Card", description: "Upload your dog's photo, choose a sport and position, and generate a custom AI sports trading card in seconds.", path: "/create" });
  const navigate = useNavigate();
  const { token } = useAuth();
  const fileInputRef = useRef(null);
  const [step, setStep] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // Form state
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [sport, setSport] = useState(null);
  const [position, setPosition] = useState(null);
  const [cardStyle, setCardStyle] = useState(null);
  const [dogName, setDogName] = useState("");
  const [breed, setBreed] = useState("");

  // In-app browser detection for /create page
  const [showInAppBanner, setShowInAppBanner] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  useEffect(() => {
    const ua = navigator.userAgent || "";
    if (/FBAN|FBAV|Instagram|FB_IAB|FBIOS|FBSS/i.test(ua)) setShowInAppBanner(true);
  }, []);

  // Compress image client-side before upload
  const compressImage = useCallback((file) => {
    return new Promise((resolve) => {
      // Skip compression for small files or HEIC (can't canvas-decode HEIC)
      if (file.size < 500000 || file.type === "image/heic" || file.type === "image/heif") {
        resolve(file);
        return;
      }
      const img = new window.Image();
      img.onload = () => {
        const maxW = 1200;
        let w = img.width;
        let h = img.height;
        if (w > maxW) {
          h = Math.round(h * (maxW / w));
          w = maxW;
        }
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        canvas.toBlob(
          (blob) => {
            if (blob && blob.size < file.size) {
              resolve(new File([blob], file.name.replace(/\.\w+$/, ".jpg"), { type: "image/jpeg" }));
            } else {
              resolve(file);
            }
          },
          "image/jpeg",
          0.85
        );
      };
      img.onerror = () => resolve(file);
      img.src = URL.createObjectURL(file);
    });
  }, []);

  // Upload with retry, timeout, and progress
  const uploadFile = useCallback(async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const timeoutId = setTimeout(() => { xhr.abort(); reject(new Error("timeout")); }, 30000);
      
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload = () => {
        clearTimeout(timeoutId);
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(JSON.parse(xhr.responseText));
        } else {
          reject(new Error(`HTTP ${xhr.status}`));
        }
      };
      xhr.onerror = () => { clearTimeout(timeoutId); reject(new Error("network")); };
      xhr.onabort = () => { clearTimeout(timeoutId); reject(new Error("aborted")); };
      
      xhr.open("POST", `${API}/cards/upload`);
      if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      xhr.send(formData);
    });
  }, [token]);

  const handleFileSelect = useCallback(async (file) => {
    if (!file) return;

    const allowed = ["image/jpeg", "image/png", "image/heic", "image/heif"];
    if (!allowed.includes(file.type)) {
      toast.error("Please upload a JPG, PNG, or HEIC file");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File must be under 10MB");
      return;
    }

    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
    setUploading(true);
    setUploadProgress(0);
    setUploadError(null);

    // Client-side compression
    const compressed = await compressImage(file);

    // Attempt upload with 1 silent retry
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        setUploadProgress(attempt > 0 ? 0 : uploadProgress);
        const resp = await uploadFile(compressed);
        setPhotoUrl(resp.url);
        setUploadProgress(100);
        setUploading(false);
        toast.success("Photo uploaded");
        return;
      } catch (err) {
        if (attempt === 0) {
          // Silent retry
          setUploadProgress(0);
          await new Promise((r) => setTimeout(r, 1000));
        }
      }
    }

    // Both attempts failed
    setUploading(false);
    setUploadError(true);
    setPhoto(null);
    setPhotoPreview(null);
  }, [compressImage, uploadFile, uploadProgress]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  }, [handleFileSelect]);

  const removePhoto = () => {
    setPhoto(null);
    setPhotoPreview(null);
    setPhotoUrl(null);
    setUploadError(null);
    setUploadProgress(0);
  };

  const handleGenerate = async () => {
    if (!photoUrl || !sport || !position || !cardStyle || !dogName.trim()) {
      toast.error("Please complete all required fields");
      return;
    }

    setGenerating(true);
    try {
      const resp = await axios.post(`${API}/cards/create`, {
        dog_name: dogName.trim(),
        breed: breed.trim() || null,
        sport,
        position,
        card_style: cardStyle,
        source_image_url: photoUrl,
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true,
      });
      navigate(`/generating/${resp.data.card_id}`);
    } catch (err) {
      toast.error("Failed to start generation. Please try again.");
      setGenerating(false);
    }
  };

  const canGoNext = () => {
    switch (step) {
      case 1: return !!photoUrl;
      case 2: return !!sport;
      case 3: return !!position;
      case 4: return !!cardStyle;
      case 5: return !!dogName.trim();
      default: return false;
    }
  };

  return (
    <div className="min-h-screen py-8 md:py-16" data-testid="create-page">
      <div className="max-w-2xl mx-auto px-4 md:px-8">
        {/* In-app browser banner — critical for /create upload flow */}
        {showInAppBanner && (
          <div className="rounded-[12px] border border-[var(--color-gold)] p-4 mb-6 flex items-start gap-3" style={{ backgroundColor: "rgba(245,166,35,0.08)" }} data-testid="create-inapp-banner">
            <span className="text-lg shrink-0 mt-0.5">📸</span>
            <div className="flex-1 min-w-0">
              <p className="font-body text-sm text-[var(--color-text-dark)]">
                For the fastest card creation, open in Chrome or Safari — photo uploads work better there!
              </p>
              <div className="flex items-center gap-3 mt-2">
                <button
                  onClick={async () => {
                    try { await navigator.clipboard.writeText(window.location.href); } catch { const i = document.createElement("input"); i.value = window.location.href; document.body.appendChild(i); i.select(); document.execCommand("copy"); document.body.removeChild(i); }
                    setUrlCopied(true); setTimeout(() => setUrlCopied(false), 2000);
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-[6px] font-body text-xs font-medium"
                  style={{ backgroundColor: "var(--color-navy)", color: "white" }}
                  data-testid="create-copy-url-btn"
                >
                  <Copy size={12} /> {urlCopied ? "Copied!" : "Copy Link"}
                </button>
                <button onClick={() => setShowInAppBanner(false)} className="font-body text-xs text-[var(--color-text-light)] hover:text-[var(--color-text-mid)]">Dismiss</button>
              </div>
            </div>
          </div>
        )}

        {/* Progress indicator */}
        <div className="flex items-center justify-center gap-2 mb-10">
          {[1, 2, 3, 4, 5].map((s) => (
            <div key={s} className="flex items-center gap-2">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center font-accent text-xs font-bold transition-colors"
                style={{
                  backgroundColor: step >= s ? "var(--color-gold)" : "var(--color-border)",
                  color: step >= s ? "var(--color-navy)" : "var(--color-text-light)",
                }}
                data-testid={`step-indicator-${s}`}
              >
                {s}
              </div>
              {s < 5 && <div className="w-8 h-0.5" style={{ backgroundColor: step > s ? "var(--color-gold)" : "var(--color-border)" }} />}
            </div>
          ))}
        </div>

        {/* Step 1: Photo Upload */}
        {step === 1 && (
          <div className="animate-slide-up" data-testid="step-upload">
            <h2 className="font-heading text-3xl sm:text-4xl uppercase tracking-tight text-[var(--color-text-dark)] text-center mb-2">
              Start With a Great Photo of Your Dog
            </h2>
            <p className="font-body text-[var(--color-text-mid)] text-center mb-8">Free preview &mdash; no payment needed until you love it.</p>

            {!photoPreview ? (
              <div>
                {/* Upload error state */}
                {uploadError && (
                  <div className="rounded-[12px] border border-[var(--color-error)] bg-red-50 p-6 text-center mb-4" data-testid="upload-error">
                    <p className="font-body text-sm text-[var(--color-error)] font-medium mb-3">Upload failed — please try again or use a smaller photo</p>
                    <button
                      onClick={() => { setUploadError(null); fileInputRef.current?.click(); }}
                      className="font-heading tracking-widest uppercase text-sm px-6 py-2.5 rounded-[8px]"
                      style={{ backgroundColor: "var(--color-gold)", color: "var(--color-navy)" }}
                      data-testid="upload-retry-btn"
                    >
                      Try Again
                    </button>
                  </div>
                )}
                {/* Upload zone */}
                <div
                  className={`upload-zone rounded-[12px] p-12 text-center cursor-pointer transition-all ${dragOver ? "drag-over" : ""} ${uploading ? "pointer-events-none opacity-60" : ""}`}
                  style={{ backgroundColor: "var(--color-white)" }}
                  onClick={() => !uploading && fileInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  data-testid="upload-zone"
                >
                  {uploading ? (
                    <>
                      <div className="w-20 h-20 rounded-full mx-auto mb-5 flex items-center justify-center" style={{ backgroundColor: "rgba(245,166,35,0.1)" }}>
                        <div className="w-8 h-8 border-3 border-[var(--color-gold)] border-t-transparent rounded-full animate-spin" />
                      </div>
                      <p className="font-body text-[var(--color-text-dark)] font-medium mb-3">Uploading... please wait</p>
                      <div className="w-48 h-2 rounded-full mx-auto" style={{ backgroundColor: "var(--color-border)" }}>
                        <div className="h-full rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%`, backgroundColor: "var(--color-gold)" }} />
                      </div>
                      <p className="font-accent text-xs text-[var(--color-text-light)] mt-2">{uploadProgress}%</p>
                    </>
                  ) : (
                    <>
                      <div className="w-20 h-20 rounded-full mx-auto mb-5 flex items-center justify-center" style={{ backgroundColor: "rgba(245,166,35,0.1)" }}>
                        <Upload size={32} style={{ color: "var(--color-gold)" }} />
                      </div>
                      <p className="font-body text-[var(--color-text-dark)] font-medium mb-1">Drag and drop your photo here</p>
                      <p className="font-body text-sm text-[var(--color-text-light)]">or click to browse &middot; JPG, PNG, HEIC &middot; Max 10MB</p>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="relative bg-[var(--color-white)] rounded-[12px] border border-[var(--color-border)] overflow-hidden">
                <img src={photoPreview} alt="Dog preview" className="w-full max-h-[400px] object-contain" data-testid="photo-preview" />
                <button
                  onClick={removePhoto}
                  disabled={uploading}
                  className="absolute top-3 right-3 w-8 h-8 rounded-full bg-[var(--color-navy)]/80 text-white flex items-center justify-center hover:bg-[var(--color-navy)] disabled:opacity-40"
                  data-testid="remove-photo-btn"
                >
                  <X size={16} />
                </button>
                {uploading && (
                  <div className="absolute inset-0 bg-white/80 flex flex-col items-center justify-center gap-3">
                    <div className="w-8 h-8 border-3 border-[var(--color-gold)] border-t-transparent rounded-full animate-spin" />
                    <p className="font-body text-sm text-[var(--color-text-dark)]">Uploading... {uploadProgress}%</p>
                    <div className="w-40 h-1.5 rounded-full" style={{ backgroundColor: "var(--color-border)" }}>
                      <div className="h-full rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%`, backgroundColor: "var(--color-gold)" }} />
                    </div>
                  </div>
                )}
                {!uploading && photoUrl && (
                  <div className="absolute bottom-3 left-3 flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ backgroundColor: "var(--color-success)" }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                    <span className="font-body text-xs font-medium text-white">Photo ready</span>
                  </div>
                )}
              </div>
            )}

            <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/heic,image/heif" className="hidden" onChange={(e) => handleFileSelect(e.target.files[0])} />

            <div className="mt-6 bg-[var(--color-white)] rounded-[12px] border border-[var(--color-border)] p-5">
              <div className="flex items-center gap-2 mb-3">
                <Camera size={16} style={{ color: "var(--color-gold)" }} />
                <span className="font-body text-sm font-medium text-[var(--color-text-dark)]">Photo tips</span>
              </div>
              <ul className="space-y-1.5">
                {["Clear face shot works best", "Natural light preferred", "No sunglasses on the dog (we know, it's tempting)"].map(tip => (
                  <li key={tip} className="font-body text-xs text-[var(--color-text-mid)] flex items-center gap-2">
                    <span className="w-1 h-1 rounded-full" style={{ backgroundColor: "var(--color-gold)" }} />{tip}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Step 2: Sport Selector */}
        {step === 2 && (
          <div className="animate-slide-up" data-testid="step-sport">
            <h2 className="font-heading text-3xl sm:text-4xl uppercase tracking-tight text-[var(--color-text-dark)] text-center mb-2">
              Choose a sport
            </h2>
            <p className="font-body text-[var(--color-text-mid)] text-center mb-8">What league is your dog joining?</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {SPORTS.map((s) => (
                <button
                  key={s.id}
                  onClick={() => { setSport(s.id); setPosition(null); }}
                  className={`bg-[var(--color-white)] rounded-[12px] border border-[var(--color-border)] p-6 text-center transition-all ${sport === s.id ? "sport-card-active" : "hover:border-[var(--color-gold)]/50"}`}
                  data-testid={`sport-${s.id}`}
                >
                  <div className="text-4xl mb-3">{s.icon}</div>
                  <p className="font-heading text-lg uppercase tracking-wider text-[var(--color-text-dark)]">{s.label}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Position Selector */}
        {step === 3 && (
          <div className="animate-slide-up" data-testid="step-position">
            <h2 className="font-heading text-3xl sm:text-4xl uppercase tracking-tight text-[var(--color-text-dark)] text-center mb-2">
              Pick a position
            </h2>
            <p className="font-body text-[var(--color-text-mid)] text-center mb-8">
              {SPORTS.find(s => s.id === sport)?.icon} {SPORTS.find(s => s.id === sport)?.label}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(POSITIONS[sport] || []).map((pos) => (
                <button
                  key={pos}
                  onClick={() => setPosition(pos)}
                  className={`bg-[var(--color-white)] rounded-[8px] border border-[var(--color-border)] px-5 py-4 text-left transition-all ${position === pos ? "sport-card-active" : "hover:border-[var(--color-gold)]/50"}`}
                  data-testid={`position-${pos.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <p className="font-body font-medium text-[var(--color-text-dark)]">{pos}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 4: Card Style */}
        {step === 4 && (
          <div className="animate-slide-up" data-testid="step-style">
            <h2 className="font-heading text-3xl sm:text-4xl uppercase tracking-tight text-[var(--color-text-dark)] text-center mb-2">
              Choose card style
            </h2>
            <p className="font-body text-[var(--color-text-mid)] text-center mb-8">How should your legend look?</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {STYLES.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setCardStyle(s.id)}
                  className={`bg-[var(--color-white)] rounded-[12px] border border-[var(--color-border)] p-6 text-center transition-all ${cardStyle === s.id ? "sport-card-active" : "hover:border-[var(--color-gold)]/50"}`}
                  data-testid={`style-${s.id}`}
                >
                  <p className="font-heading text-xl uppercase tracking-wider text-[var(--color-text-dark)] mb-2">{s.label}</p>
                  <p className="font-body text-xs text-[var(--color-text-mid)]">{s.desc}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 5: Dog Details */}
        {step === 5 && (
          <div className="animate-slide-up" data-testid="step-details">
            <h2 className="font-heading text-3xl sm:text-4xl uppercase tracking-tight text-[var(--color-text-dark)] text-center mb-2">
              Name your legend
            </h2>
            <p className="font-body text-[var(--color-text-mid)] text-center mb-8">This name will appear on the trading card</p>
            <div className="bg-[var(--color-white)] rounded-[12px] border border-[var(--color-border)] p-6 space-y-5">
              <div>
                <Label className="font-body text-sm font-medium text-[var(--color-text-dark)] mb-1.5 block">
                  Dog's name <span className="text-[var(--color-error)]">*</span>
                </Label>
                <Input
                  value={dogName}
                  onChange={(e) => setDogName(e.target.value)}
                  placeholder="e.g. Max, Bella, Cooper"
                  className="rounded-[8px] border-[var(--color-border)] focus:ring-[var(--color-gold)]"
                  data-testid="dog-name-input"
                />
              </div>
              <div>
                <Label className="font-body text-sm font-medium text-[var(--color-text-dark)] mb-1.5 block">
                  Breed <span className="text-[var(--color-text-light)] text-xs">(optional)</span>
                </Label>
                <Input
                  value={breed}
                  onChange={(e) => setBreed(e.target.value)}
                  placeholder="e.g. Golden Retriever, Husky"
                  className="rounded-[8px] border-[var(--color-border)] focus:ring-[var(--color-gold)]"
                  data-testid="breed-input"
                />
                <p className="font-body text-xs text-[var(--color-text-light)] mt-1">Helps our AI create a more accurate card</p>
              </div>

              {/* Summary */}
              <div className="border-t border-[var(--color-border)] pt-5 mt-5">
                <p className="font-accent text-xs tracking-widest uppercase mb-3" style={{ color: "var(--color-gold)" }}>Card summary</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="font-body text-xs text-[var(--color-text-light)]">Sport</p>
                    <p className="font-body text-sm text-[var(--color-text-dark)] capitalize">{sport}</p>
                  </div>
                  <div>
                    <p className="font-body text-xs text-[var(--color-text-light)]">Position</p>
                    <p className="font-body text-sm text-[var(--color-text-dark)]">{position}</p>
                  </div>
                  <div>
                    <p className="font-body text-xs text-[var(--color-text-light)]">Style</p>
                    <p className="font-body text-sm text-[var(--color-text-dark)] capitalize">{cardStyle}</p>
                  </div>
                  {photoPreview && (
                    <div>
                      <p className="font-body text-xs text-[var(--color-text-light)]">Photo</p>
                      <img src={photoPreview} alt="" className="w-10 h-10 rounded object-cover" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation buttons */}
        <div className="flex items-center justify-between mt-8 gap-4">
          {step > 1 ? (
            <button
              onClick={() => setStep(step - 1)}
              className="flex items-center gap-1 font-body text-sm text-[var(--color-text-mid)] hover:text-[var(--color-text-dark)] transition-colors"
              data-testid="back-btn"
            >
              <ChevronLeft size={16} /> Back
            </button>
          ) : <div />}

          {step < 5 ? (
            <button
              onClick={() => canGoNext() && setStep(step + 1)}
              disabled={!canGoNext() || uploading}
              className="flex items-center gap-1 font-heading tracking-widest uppercase px-8 py-3 rounded-[8px] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ backgroundColor: "var(--color-gold)", color: "var(--color-navy)" }}
              data-testid="next-btn"
            >
              Next <ChevronRight size={16} />
            </button>
          ) : (
            <button
              onClick={handleGenerate}
              disabled={!canGoNext() || generating}
              className="flex items-center gap-2 font-heading tracking-widest uppercase px-8 py-3 rounded-[8px] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ backgroundColor: "var(--color-gold)", color: "var(--color-navy)" }}
              data-testid="generate-btn"
            >
              {generating ? (
                <>
                  <div className="w-4 h-4 border-2 border-[var(--color-navy)] border-t-transparent rounded-full animate-spin" />
                  Generating...
                </>
              ) : (
                "Generate Preview"
              )}
            </button>
          )}
        </div>

        <p className="font-body text-xs text-[var(--color-text-light)] text-center mt-4">
          {step === 1 ? "Step 1 of 5 \u2014 takes less than 2 minutes total" : `Step ${step} of 5`}
        </p>
      </div>
    </div>
  );
}
